import React, { FC, useRef } from 'react';
import classnames from 'classnames';
import EmojiPicker from '@src/components/EmojiPicker';
import { parseEmojis } from '@src/util/parseEmojis';
import { Button } from '@src/components/Button';
import { MetricAction, MetricContext, MetricEventSource } from '@src/metrics/metrics.enum';
import { MessageThreadStateEnum } from '@src/graphql/generated';
import { trackEvent } from '@src/metrics';
import { ButtonProps } from './interfaces/buttonProps.interface';
import { MessageEntryAreaWithoutQuickActionsProps } from './props';
import '../styles.scss';

const TEXT_AREA_HEIGHT = 2.4;
const CHAT_CLOSED_MESSAGE = 'This chat has been closed';

export const MessageEntryAreaWithoutQuickActions: FC<MessageEntryAreaWithoutQuickActionsProps> = ({
  message,
  metricsContext,
  onChange,
  onSend,
  isDisabled,
  onClose,
  onReopen,
  messageThread,
}) => {
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);

  const isChatClosedByCandidate = messageThread.userState === MessageThreadStateEnum.CLOSED;
  const isChatClosedByRecruiter = messageThread.recruiterState === MessageThreadStateEnum.CLOSED;
  const isChatClosed = isChatClosedByCandidate || isChatClosedByRecruiter;

  const handleSend = (): void => {
    onSend();

    trackEvent({
      context: MetricContext.CHAT,
      interaction: MetricAction.SEND_CHAT_MESSAGE,
      payload: {
        'Event Source': MetricEventSource.SEND_BUTTON_IN_INPUT_FIELD,
        'Previous Context': metricsContext,
        'Thread Id': messageThread.id,
      },
    });
  };

  const handleClose = (): void => {
    if (onClose) {
      onClose();

      trackEvent({
        context: MetricContext.END_CHAT,
        interaction: MetricAction.END_CHAT,
        payload: {
          'Thread Id': messageThread.id,
          'Type': 'Close',
        },
      });
    }
  };

  const handleReopen = (): void => {
    if (onReopen) {
      onReopen();

      trackEvent({
        context: MetricContext.CHAT,
        interaction: MetricAction.REOPEN_CHAT,
        payload: {
          'Event Source': MetricEventSource.REOPEN_BUTTON_IN_INPUT_FIELD,
          'Previous Context': metricsContext,
          'Thread Id': messageThread.id,
        },
      });
    }
  };

  const onChangeHandler = (updatedValue: string): void => {
    const parsedVal = parseEmojis(updatedValue);

    if (textAreaRef.current) {
      textAreaRef.current.style.height = TEXT_AREA_HEIGHT + 'rem';
      textAreaRef.current.style.height = textAreaRef.current.scrollHeight / 10 + 'rem';
    }

    onChange(parsedVal);
  };

  const clickHandler = (action: () => void): void => {
    if (textAreaRef.current) {
      textAreaRef.current.style.height = TEXT_AREA_HEIGHT + 'rem';
    }

    action();
  };

  const BUTTON_TYPES: Record<string, ButtonProps[]> = {
    closedByCandidate: [
      {
        disabled: false,
        handleClick: () => clickHandler(handleClose),
        label: 'Close',
      },
    ],
    closedByRecruiter: [
      {
        disabled: false,
        handleClick: () => clickHandler(handleReopen),
        label: 'Reopen',
      },
    ],
    open: [
      {
        disabled: isDisabled,
        handleClick: () => clickHandler(handleSend),
        label: 'Send',
      },
    ],
  };

  const isMultiline = message.split(/\r*\n/).length > 1;

  let buttonsToShow = messageThread.recruiterState ? BUTTON_TYPES[messageThread.recruiterState] : [];

  let closedMessage = '';

  if (isChatClosedByCandidate) {
    buttonsToShow = onClose ? BUTTON_TYPES.closedByCandidate : [];
    closedMessage = 'This chat has been closed by applicant.';
  }

  if (isChatClosedByRecruiter) {
    buttonsToShow = onReopen ? BUTTON_TYPES.closedByRecruiter : [];
    closedMessage = CHAT_CLOSED_MESSAGE;
  }

  // hide buttons if chat is closed from both sides
  if (isChatClosedByCandidate && isChatClosedByRecruiter) {
    buttonsToShow = [];
    closedMessage = CHAT_CLOSED_MESSAGE;
  }

  return (
    <div className="message-entry-area">
      {!isChatClosed && (
        <div className="message-entry-area__icon-container">
          <EmojiPicker
            metricsContext={metricsContext}
            onSelect={emoji => onChangeHandler(message + emoji)}
            threadId={messageThread.id}
          />
        </div>
      )}

      <div className="message-entry-area__text-container">
        <textarea
          className={classnames(
            'message-entry-area__textarea',
            isMultiline && 'message-entry-area__textarea--multiline',
          )}
          disabled={isChatClosed}
          onChange={e => onChangeHandler(e.target.value)}
          onKeyDown={e => {
            // send message on enter click
            if (e.keyCode === 13 && !e.shiftKey) {
              e.preventDefault();

              if (isDisabled) {
                return;
              }

              handleSend();
            }
          }}
          placeholder="Type message"
          ref={textAreaRef}
          style={{ height: TEXT_AREA_HEIGHT + 'rem' }}
          value={isChatClosed ? closedMessage : message}
        />
      </div>

      <div className="message-entry-area__button-container">
        {buttonsToShow?.map((buttonProps, index) => {
          return (
            <Button
              className="message-entry-area__button"
              isDisabled={buttonProps.disabled}
              key={index}
              onClick={buttonProps.handleClick}
            >
              {buttonProps.label}
            </Button>
          );
        })}
      </div>
    </div>
  );
};
